import { useEffect } from 'react';
import { AutoComplete } from 'antd';
import styled from 'styled-components';
import { CommonUtility } from '../../utility';
import {
  CustomerAutoCompleteHook,
  useAutocompleteInput,
  useAutocompleteLoading,
  useDebounce,
} from '../../hooks';
import { OrganisationAvatar, AutocompleteSearchInput } from '../../components';

const OptionContainer = styled.div`
  padding: 8px 12px;
`;

function resultRenderer({ customerOrganizationId, organizationName, logo, search }) {
  const showResult = string => {
    try {
      const highlighted = string.replace(
        new RegExp(search, 'gi'),
        d => `<strong>${d}</strong>`,
      );

      return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
    } catch (error) {
      return string;
    }
  };

  return (
    <OptionContainer className="d-flex flex-column" key={customerOrganizationId}>
      <div className="d-flex align-items-center">
        <OrganisationAvatar mini name={organizationName} image={logo?.url} />
        <span className="ml-2">{showResult(organizationName)}</span>
      </div>
    </OptionContainer>
  );
}

export function CustomerSearchInput({
  onResultClick,
  inputClassName = undefined,
  style = undefined,
  setTableSearch = () => {},
  size = undefined,
  clearInput = false,
  refreshKey = undefined,
}) {
  const { display, query, onSearch, onSelect, clear } =
    useAutocompleteInput(setTableSearch);

  const finalSearch = useDebounce(query, 700);
  const { data, loading } = CustomerAutoCompleteHook(finalSearch);
  const isSearching = useAutocompleteLoading(query, finalSearch, loading);

  const optionIds = (data ?? [])
    .map(d => String(d.customerOrganizationId ?? d._id ?? ''))
    .filter(Boolean);

  const options = (data ?? []).map(item => ({
    value: String(item.customerOrganizationId ?? item._id ?? ''),
    label: resultRenderer({ ...item, search: display }),
  }));

  const resetSearch = () => {
    clear();
    onResultClick({});
  };

  useEffect(() => {
    if (CommonUtility.isNotEmpty(refreshKey)) {
      resetSearch();
    }
  }, [refreshKey]);

  return (
    <AutoComplete
      className={inputClassName}
      style={style}
      value={display}
      options={options}
      filterOption={false}
      placeholder="Customer Name"
      classNames={{ popup: { root: 'auto-search-dropdown' } }}
      onSearch={value => onSearch(value, optionIds)}
      onSelect={value => {
        const item = data.find(
          d => String(d.customerOrganizationId ?? d._id ?? '') === value,
        );
        if (item?.customerOrganizationId || item?._id) {
          onSelect(clearInput ? '' : item.organizationName, () => onResultClick(item));
        }
      }}
    >
      <AutocompleteSearchInput
        size={size}
        isSearching={isSearching}
        onClear={resetSearch}
      />
    </AutoComplete>
  );
}
